/**
 * Admin clients for estimate sharing (docs/ESTIMATE-SHARING-PLAN.md):
 *   admin-shares.php          — tracking list, events, resend / restore / remove / test send
 *   admin-share-settings.php  — the super-admin settings card (handler URL, sender, redirect)
 * Credentialed (admin session). Admin pages are English-only.
 */

import type { ShareKind } from '../types/share';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data as { error?: string; message?: string; fields?: string[] };
    throw new Error(d.message || d.error || `Request failed (${res.status})`);
  }
  return data as T;
}

/* ---------------------------------------------------------------- tracking */

export interface AdminShare {
  id: number;
  guid: string | null;
  shareUrl: string | null;
  kind: ShareKind;
  locationName: string;
  locationId: number | null;
  sfid: string;
  publicId: string | null;
  city: string | null;
  region: string | null;
  emailMasked: string;
  status: 'active' | 'removed';
  promoted: boolean;
  grantTargets: 'all' | number[];
  sentAt: string;
  lastSentAt: string | null;
  sendCount: number;
  lastSendStatus: 'ok' | 'failed' | 'skipped' | null;
  viewCount: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  commentCount: number;
  lastCommentAt: string | null;
  redirected: boolean;
  removedAt: string | null;
  estimate: { id: number; guid: string; name: string | null; customerName: string; customerEmail: string | null; deleted: boolean; locale: string | null };
  sender: { name: string | null; email: string | null; userId: number | null };
  location: { id: number | null; name: string; sfid: string; kind: ShareKind; email: string | null; emailOverride: string | null; city: string | null; region: string | null; country: string | null; isActive: boolean | null };
  retailerAtShare: { id: number; name: string | null } | null;
  retailerNow: { id: number; name: string; isActive: boolean } | null;
  message: string | null;
  locale: string | null;
  removedByKind: string | null;
  lastEvent: { type: string; at: string; error: string | null } | null;
}

export interface ShareEvent {
  id: number;
  estimateId: number;
  shareId: number | null;
  type: string;
  actorKind: string;
  actorUserId: number | null;
  provider: string | null;
  intendedTo: string | null;
  effectiveTo: string | null;
  redirectReason: string | null;
  httpStatus: number | null;
  providerRef: string | null;
  error: string | null;
  payload: Record<string, unknown> | null;
  ip: string | null;
  at: string;
  estimateName?: string | null;
  locationName?: string | null;
}

export interface SharePagination { page: number; perPage: number; total: number; pages: number }

export interface AdminShareStats { sentToday: number; failedToday: number; skippedToday: number; activeShares: number; viewed: number; commented: number }

export interface AdminSharesQuery {
  page?: number; perPage?: number; search?: string; status?: '' | 'active' | 'removed'; kind?: '' | ShareKind;
  sendStatus?: '' | 'ok' | 'failed' | 'skipped'; provider?: string; viewed?: '' | 'yes' | 'no';
  from?: string; to?: string; includeRemoved?: boolean; sort?: string; order?: 'asc' | 'desc';
}

export async function fetchAdminShares(q: AdminSharesQuery): Promise<{ shares: AdminShare[]; pagination: SharePagination; stats: AdminShareStats; settings: { redirectActive: boolean } }> {
  const p = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== false) p.set(k, String(v === true ? 1 : v)); });
  const res = await fetch(`${API_BASE}/admin-shares.php?${p.toString()}`, { credentials: 'include' });
  return handle(res);
}

export async function fetchAdminShare(id: number): Promise<{ share: AdminShare; events: ShareEvent[] }> {
  const res = await fetch(`${API_BASE}/admin-shares.php?id=${id}`, { credentials: 'include' });
  return handle(res);
}

export async function fetchShareEvents(q: { page?: number; perPage?: number; type?: string; from?: string; to?: string }): Promise<{ events: ShareEvent[]; pagination: SharePagination }> {
  const p = new URLSearchParams({ events: '1' });
  Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') p.set(k, String(v)); });
  const res = await fetch(`${API_BASE}/admin-shares.php?${p.toString()}`, { credentials: 'include' });
  return handle(res);
}

export async function adminShareAction(action: 'resend' | 'restore' | 'remove' | 'test-send', id: number, body: Record<string, unknown> = {}): Promise<{ ok: boolean; status?: string; error?: string | null; provider?: string; redirected?: boolean }> {
  const res = await fetch(`${API_BASE}/admin-shares.php?action=${action}&id=${id}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return handle(res);
}

/* ---------------------------------------------------------------- settings */

export interface ShareSettings {
  shareEnabled: boolean;
  shareSenderProvider: 'pardot' | 'smtp';
  shareFormHandlerUrl: string;
  shareFriendlyName: string;
  shareEmailRedirectTo: string;
  shareSmtpFallbackEnabled: boolean;
  shareMaxPerEstimatePerHour: number;
  shareMaxPerEstimateTotal: number;
}

export interface ShareSettingsView {
  settings: ShareSettings;
  provider: { name: string; configured: boolean; warnings: string[] };
  redirect: { active: boolean; to: string; warnings: string[] };
  smtpConfigured: boolean;
  senders: Record<string, { label: string; hint: string; needs: string[] }>;
  pardotFields: string[];
}

export async function fetchShareSettings(): Promise<ShareSettingsView> {
  const res = await fetch(`${API_BASE}/admin-share-settings.php`, { credentials: 'include' });
  return handle(res);
}

export async function saveShareSettings(patch: Partial<ShareSettings>): Promise<ShareSettingsView> {
  const res = await fetch(`${API_BASE}/admin-share-settings.php`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings: patch }),
  });
  return handle(res);
}

/** Window event the settings card fires after a save so the dashboard banner refreshes. */
export const SHARE_SETTINGS_CHANGED = 'share-settings-changed';
